import { RolesApi, UsersApi } from '@api';
import { Box, Card, Flex, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import {
  Field,
  fieldContentStyle,
  FormController,
  FormInput,
  FormPasswordInput,
  formSchemas,
  RenderFormElement,
} from '@components/form';
import { UsersType } from '@types';
import { CheckBoxGroup, OverlayDialog, SwitchOnOff, toasterApi } from '@ui';
import React, { useState } from 'react';
import { Controller, FieldErrors } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  user: UsersType.UserPartial;
  setUser?: (user: UsersType.UserPartial) => void;
  isNew?: boolean;
}

type FormData =
  | z.infer<typeof formSchemaPost>
  | z.infer<typeof formSchemaPatch>;

const ManageCard: React.FC<Props> = ({ user, setUser, isNew = false }) => {
  const [patchUser] = UsersApi.Api.usePatchUserMutation();
  const [postUser] = UsersApi.Api.usePostUserMutation();

  const [enableEdit, setEnableEdit] = useState(isNew);

  const { data: roles } = RolesApi.Api.useGetAllRolesQuery();

  const onFormSubmit = async (formData: FormData) => {
    if (!isNew) {
      const nextName = (
        'display_name' in formData ? formData.display_name : ''
      )
        ?.toString()
        .trim();
      const currentName = user.display_name?.toString().trim();
      if (!nextName || nextName === currentName) {
        toasterApi({
          type: 'warning',
          message: 'No changes to update',
        });
        return;
      }
    }

    OverlayDialog.open('confirmUserUpdate', {
      overlayId: 'confirmUserUpdate',
      title: `Confirm ${isNew ? 'User Creation' : 'User Update'}`,
      description: `Are you sure you want to ${
        isNew ? 'create' : 'update'
      } the user?`,
      content: (
        <VStack align="flex-start" gap={2}>
          <Text>
            This will {isNew ? 'create' : 'update'} the user's details.
          </Text>
          <RenderFormElement
            previousValue={isNew ? undefined : user?.display_name}
            currentValue={
              'display_name' in formData
                ? formData.display_name
                : user?.display_name
            }
            label="Full Name"
          />
          {isNew && (
            <>
              <RenderFormElement
                currentValue={
                  'username' in formData ? formData.username : user?.username
                }
                label="User Name"
              />
              <RenderFormElement
                currentValue={'email' in formData ? formData.email : user?.email}
                label="Email"
              />
              <RenderFormElement
                currentValue={
                  'password' in formData && formData.password
                    ? '********(new password)'
                    : '********'
                }
                label="Password"
              />
              <RenderFormElement
                currentValue={
                  'roles' in formData
                    ? formData.roles
                        ?.map((r) => roles?.find((role) => role.id === r)?.name)
                        .join(', ')
                    : user?.roles?.map((r) => r.name).join(', ')
                }
                label="Roles"
              />
            </>
          )}
        </VStack>
      ),
      handleConfirm: async () => {
        try {
          const result =
            user.id === 0
              ? await postUser({
                  display_name:
                    'display_name' in formData
                      ? formData.display_name
                      : undefined,
                  username:
                    'username' in formData ? formData.username : undefined,
                  email: 'email' in formData ? formData.email : undefined,
                  password:
                    'password' in formData ? formData.password : undefined,
                  roles: 'roles' in formData ? formData.roles : undefined,
                }).unwrap()
              : await patchUser({
                  userId: user.id!,
                  args: {
                    ...('display_name' in formData &&
                      formData.display_name && {
                        display_name: formData.display_name,
                      }),
                  },
                }).unwrap();

          setUser?.(result);

          toasterApi({
            type: 'success',
            message: `User ${isNew ? 'created' : 'updated'} successfully`,
          });

          if (!isNew) {
            setEnableEdit(false);
          }
        } catch (err: any) {
          toasterApi({
            type: 'error',
            message:
              err?.data?.detail ||
              err?.data?.message ||
              `Failed to ${isNew ? 'create' : 'update'} user`,
          });
        }
      },
    });
  };

  return (
    <>
      <Stack>
        {!isNew && (
          <HStack alignSelf={'flex-end'} gap={8}>
            <Text textStyle={'heading.card'} color={'tertiary.fg'}>
              Edit User Details
            </Text>
            <SwitchOnOff value={enableEdit} onValueChange={setEnableEdit} />
          </HStack>
        )}
        <Card.Root width="100%" variant="elevated">
          <Card.Body gap="7">
            <FormController
              key={`form-${isNew ? 'post' : 'patch'}-${user.id}-${enableEdit}`}
              label={isNew ? 'Create User' : 'Edit User Details'}
              formSchema={isNew ? formSchemaPost : formSchemaPatch}
              helperText={
                isNew
                  ? 'Please fill out the form to create a new user.'
                  : !enableEdit
                    ? 'Please Enable Edit Mode'
                    : 'Please fill out the form to update user details.'
              }
              onFormSubmit={onFormSubmit}
              disabled={isNew && user.id !== 0 ? true : !enableEdit}
              defaultValues={
                isNew
                  ? ({
                      display_name: '',
                      username: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      roles: [],
                    } as z.infer<typeof formSchemaPost>)
                  : ({
                      display_name: user?.display_name ?? '',
                    } as z.infer<typeof formSchemaPatch>)
              }
              buttonProps={{
                variant: 'outline',
                color: 'tertiary.fg',
                width: '100%',
                mt: '4',
              }}
            >
              {({ register, control, errors }) => {
                return (
                  <>
                    <FormInput
                      control={control}
                      disabled={isNew && user.id !== 0 ? true : !enableEdit}
                      label="Full Name"
                      id="display_name"
                      register={register}
                      errors={errors}
                      helperText={formSchemas.userSchema.nameHelper}
                      autoComplete="name"
                      required
                      defaultValue={isNew ? '' : user?.display_name}
                    />
                    {isNew && (
                      <>
                        <FormInput
                          control={control}
                          disabled={user.id !== 0 ? true : !enableEdit}
                          label="User Name"
                          id="username"
                          register={register}
                          errors={errors}
                          helperText={formSchemas.userSchema.usernameHelper}
                          autoComplete="username"
                          required
                        />
                        <FormInput
                          control={control}
                          disabled={user.id !== 0 ? true : !enableEdit}
                          label="Email"
                          id="email"
                          type="email"
                          register={register}
                          errors={errors}
                          helperText={formSchemas.directoryScheme.emailHelper}
                          autoComplete="email"
                          required
                        />
                        <FormPasswordInput
                          control={control}
                          disabled={user.id !== 0 ? true : !enableEdit}
                          label="Password"
                          id="password"
                          autoComplete="new-password"
                          register={register}
                          errors={errors}
                          helperText={formSchemas.userSchema.passwordHelper}
                          required
                        />
                        <FormPasswordInput
                          control={control}
                          disabled={user.id !== 0 ? true : !enableEdit}
                          label="Confirm Password"
                          id="confirmPassword"
                          autoComplete="new-password"
                          register={register}
                          errors={errors}
                          helperText={formSchemas.userSchema.confirmPasswordHelper}
                          required
                        />
                        <Flex>
                          <Box maxWidth="262px">
                            <Field
                              label="Permissible Roles"
                              invalid={
                                !!(
                                  errors as FieldErrors<
                                    z.infer<typeof formSchemaPost>
                                  >
                                )?.roles?.message
                              }
                              errorText={
                                (
                                  errors as FieldErrors<
                                    z.infer<typeof formSchemaPost>
                                  >
                                )?.roles?.message
                              }
                              required
                              textStyle="fieldContent"
                              {...(fieldContentStyle as any)}
                            />
                          </Box>

                          <Controller
                            control={control}
                            name="roles"
                            disabled={user.id !== 0 ? true : !enableEdit}
                            render={({ field }) => (
                              <CheckBoxGroup
                                items={roles ?? []}
                                keyForDisplay="name"
                                keyForValue="id"
                                multiple={true}
                                useSelectAllOption={false}
                                layout="wrap"
                                onSelect={field.onChange}
                                itemContainerProps={{ px: 3 }}
                                disabled={!enableEdit}
                                defaultValue={[]}
                              />
                            )}
                          />
                        </Flex>
                      </>
                    )}
                  </>
                );
              }}
            </FormController>
          </Card.Body>
        </Card.Root>
      </Stack>
    </>
  );
};

export default ManageCard;

const formSchemaPost = z
  .object({
    display_name: formSchemas.userSchema.nameSchema,
    username: formSchemas.userSchema.usernameSchema,
    email: formSchemas.directoryScheme.emailSchema,
    password: formSchemas.userSchema.passwordSchema,
    confirmPassword: formSchemas.userSchema.passwordSchema,
    roles: z
      .array(z.number())
      .min(1, { message: 'Please select at least one role' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const formSchemaPatch = z.object({
  display_name: formSchemas.userSchema.nameSchema,
});
